/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Header, Icon, Input, Message, Segment, Table } from 'semantic-ui-react';
import { createSupportPreparation, fetchSupportPreparations } from '../../utils/apiCalls';
import { getAPIToken } from '../../utils';
import { formatApiError } from '../../utils/apiErrors';

type PreparationListItem = {
  id: string;
  type: number;
  manufacturer: string;
  name: string;
  cultivar: string;
  form: number;
  thc: string;
  cbd: string;
};

const typeOptions = [
  { key: 'FLOWER', text: 'Flower', value: 'FLOWER' },
  { key: 'EXTRACT', text: 'Extract', value: 'EXTRACT' },
  { key: 'VAPE', text: 'Vape', value: 'VAPE' },
];

const formOptions = [
  { key: 'INDICA', text: 'Indica', value: 'INDICA' },
  { key: 'SATIVA', text: 'Sativa', value: 'SATIVA' },
  { key: 'HYBRID', text: 'Hybrid', value: 'HYBRID' },
  { key: 'HYBRID_SATIVA_DOMINANT', text: 'Hybrid Sativa-Dominant', value: 'HYBRID_SATIVA_DOMINANT' },
  { key: 'HYBRID_INDICA_DOMINANT', text: 'Hybrid Indica-Dominant', value: 'HYBRID_INDICA_DOMINANT' },
];

const typeLabel: Record<number, string> = { 0: 'Flower', 1: 'Extract', 2: 'Vape' };
const formLabel: Record<number, string> = {
  0: 'Indica',
  1: 'Sativa',
  2: 'Hybrid',
  3: 'Hybrid Sativa-Dominant',
  4: 'Hybrid Indica-Dominant',
};

const defaultFormState = {
  manufacturer: '',
  name: '',
  cultivar: '',
  type: 'FLOWER',
  form: 'INDICA',
  thc: '',
  cbd: '',
};

export default function PreparationsComponent() {
  const [preparations, setPreparations] = useState<PreparationListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  const [formFilter, setFormFilter] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formState, setFormState] = useState(defaultFormState);

  const token = useMemo(() => getAPIToken(), []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const loadPreparations = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchSupportPreparations({
          token,
          page,
          pageSize,
          q: debouncedSearch,
          type: typeFilter,
          form: formFilter,
        });
        if (!response || !(response.status >= 200 && response.status < 300) || !response.data) {
          throw response;
        }
        setPreparations(response.data.data || []);
        setTotal(response.data.total || 0);
      } catch (failure) {
        setPreparations([]);
        setTotal(0);
        setError(`Could not load preparations.\n${formatApiError(failure)}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreparations();
  }, [debouncedSearch, formFilter, page, pageSize, token, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const onChangeForm = (e: React.ChangeEvent<HTMLInputElement> | any, { name, value }: any) => {
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const validateNumberField = (value: string) => {
    if (value === undefined || value === null) return false;
    const normalized = value.replace(',', '.').trim();
    return normalized !== '' && !Number.isNaN(Number.parseFloat(normalized));
  };

  const resetForm = () => {
    setFormState(defaultFormState);
  };

  const handleCreatePreparation = async () => {
    setError(null);
    setSuccessMessage(null);
    const { manufacturer, name, cultivar, type, form, thc, cbd } = formState;
    if (!manufacturer.trim() || !name.trim() || !cultivar.trim()) {
      setError('Manufacturer, name, and cultivar are required.');
      return;
    }
    if (!validateNumberField(thc) || !validateNumberField(cbd)) {
      setError('THC and CBD must be numeric values.');
      return;
    }

    const payload = {
      manufacturer: manufacturer.trim(),
      name: name.trim(),
      cultivar: cultivar.trim(),
      type,
      form,
      thc,
      cbd,
    };

    try {
      const response = await createSupportPreparation({ token, payload });
      if (!response || !(response.status >= 200 && response.status < 300) || !response.data) {
        throw response;
      }
      setSuccessMessage('Preparation created successfully.');
      resetForm();
      setPage(1);
    } catch (failure) {
      setError(`Could not create preparation.\n${formatApiError(failure)}`);
    }
  };

  return (
    <div className='details_container'>
      <Header as='h2'>
        <Header.Content>Preparations</Header.Content>
      </Header>

      <Segment>
        <Header as='h4'>Search & Filters</Header>
        <Form>
          <Form.Field>
            <label>Search</label>
            <Input
              icon='search'
              placeholder='Name, manufacturer, cultivar'
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
          </Form.Field>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>Type</label>
              <Dropdown
                placeholder='All'
                selection
                clearable
                options={typeOptions}
                value={typeFilter}
                onChange={(e, data) => {
                  setPage(1);
                  setTypeFilter(data.value as string | undefined);
                }}
              />
            </Form.Field>
            <Form.Field>
              <label>Form</label>
              <Dropdown
                placeholder='All'
                selection
                clearable
                options={formOptions}
                value={formFilter}
                onChange={(e, data) => {
                  setPage(1);
                  setFormFilter(data.value as string | undefined);
                }}
              />
            </Form.Field>
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>Page Size</label>
              <Dropdown
                selection
                options={[
                  { key: 10, text: '10', value: 10 },
                  { key: 20, text: '20', value: 20 },
                  { key: 50, text: '50', value: 50 },
                  { key: 100, text: '100', value: 100 },
                ]}
                value={pageSize}
                onChange={(e, data) => {
                  setPageSize(Number(data.value));
                  setPage(1);
                }}
              />
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>

      <Segment loading={isLoading}>
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Manufacturer</Table.HeaderCell>
              <Table.HeaderCell>Cultivar</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Form</Table.HeaderCell>
              <Table.HeaderCell>THC</Table.HeaderCell>
              <Table.HeaderCell>CBD</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {preparations.map((prep) => (
              <Table.Row key={prep.id}>
                <Table.Cell>{prep.name}</Table.Cell>
                <Table.Cell>{prep.manufacturer}</Table.Cell>
                <Table.Cell>{prep.cultivar}</Table.Cell>
                <Table.Cell>{typeLabel[prep.type] ?? prep.type}</Table.Cell>
                <Table.Cell>{formLabel[prep.form] ?? prep.form}</Table.Cell>
                <Table.Cell>{prep.thc}</Table.Cell>
                <Table.Cell>{prep.cbd}</Table.Cell>
              </Table.Row>
            ))}
            {preparations.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan='7' textAlign='center'>
                  No preparations found.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            Page {page} of {totalPages} ({total} total)
          </span>
          <Button.Group>
            <Button icon='chevron left' disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))} />
            <Button
              icon='chevron right'
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            />
          </Button.Group>
        </div>
      </Segment>

      <Segment>
        <Header as='h4'>Add Preparation</Header>
        {successMessage && (
          <Message positive>
            <Message.Header>Success</Message.Header>
            <p>{successMessage}</p>
          </Message>
        )}
        {error && (
          <Message negative role='alert' aria-label='Preparation error'>
            <Message.Header>Error</Message.Header>
            <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{error}</pre>
          </Message>
        )}
        <Form>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>Manufacturer</label>
              <Input name='manufacturer' value={formState.manufacturer} onChange={onChangeForm} />
            </Form.Field>
            <Form.Field>
              <label>Name</label>
              <Input name='name' value={formState.name} onChange={onChangeForm} />
            </Form.Field>
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>Cultivar</label>
              <Input name='cultivar' value={formState.cultivar} onChange={onChangeForm} />
            </Form.Field>
            <Form.Field>
              <label>Type</label>
              <Dropdown name='type' selection options={typeOptions} value={formState.type} onChange={onChangeForm} />
            </Form.Field>
            <Form.Field>
              <label>Form</label>
              <Dropdown name='form' selection options={formOptions} value={formState.form} onChange={onChangeForm} />
            </Form.Field>
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>THC</label>
              <Input name='thc' value={formState.thc} onChange={onChangeForm} placeholder='e.g., 20.0' />
            </Form.Field>
            <Form.Field>
              <label>CBD</label>
              <Input name='cbd' value={formState.cbd} onChange={onChangeForm} placeholder='e.g., 1.0' />
            </Form.Field>
          </Form.Group>
          <Button primary onClick={handleCreatePreparation}>
            <Icon name='add' />
            Create Preparation
          </Button>
        </Form>
      </Segment>
    </div>
  );
}
